'use client';

import ButtonsHideAmount from '@components/buttons-hide-amount/ButtonsHideAmount';
import { RedPriceTooltip } from '@components/red-price-tooltip/RedPriceTooltip';
import order from '@images/renders/icon_images/icon-lock.svg';
import installmentIcon from '@images/renders/icon_images/installment.svg';
import redPriceIcon from '@images/renders/icon_images/promo.svg';
import { RESERVED_FLATS_31832, SETTLEMENT_INFO_29919 } from '@lib/flags';
import { flatsLocalStorageSelector, removeLocalFlats, setLocalFlats } from '@lib/store/slices/FavoriteFlatSlice';
import { setGenPlan } from '@lib/store/slices/FlatSlice';
import changeDate from '@lib/tools/_change_date';
import editRoomsLabel from '@lib/tools/_edit-rooms-label';
import formatLongPrice from '@lib/tools/_format-long-price';
import { SCHEMA, URLS } from '@lib/tools/helpers/constants';
import { pushDataLayer } from '@lib/tools/pushDataLayer';
import cn from 'classnames';
import NextImage from 'next/image';
import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import style from './FlatDescription.module.scss';
const RESERVED_TOOLTIP_TEXT = 'Квартира забронирована на 7 дней, но она может освободиться. Подпишитесь на нее, чтобы не упустить.';
const renderSchemaMetaOffer = (isSchema, currentPrice) => {
  if (!isSchema) return null;
  return <>
      <meta itemProp='priceCurrency' content='RUB' />
      <meta itemProp='price' content={String(currentPrice)} />
      <meta itemProp='availability' content={SCHEMA.IN_STOCK} />
    </>;
};
const renderReservedStatus = useTooltip => {
  if (useTooltip) {
    return <div className={style.flatSquare__sold}>
        <div className={cn(style.flatSquare__soldImg, style.flatSquare__soldImg_new, style.flatSquare__imgWrapper)} data-tooltip={RESERVED_TOOLTIP_TEXT}>
          <div className={style.flatSquare__lock}>
            <NextImage fill src={order} alt='order' />
          </div>
        </div>
      </div>;
  }
  return <div className={style.flatSquare__sold}>
      <div className={style.flatSquare__soldImg}>
        <div className={style.flatSquare__lock}>
          <NextImage fill src={order} alt='order' />
        </div>
        <p className={style.flatSquare__soldText}>Забронировано</p>
      </div>
    </div>;
};
const renderPriceBlock = (flat, isSchema) => {
  const isRedPrice = Boolean(flat?.redPrice);
  const tooltipText = isRedPrice ? 'Только при 100% оплате или ипотеке без субсидирования' : 'Выгодная рассрочка';
  return <RedPriceTooltip text={tooltipText} someStyles={style.tooltip}>
      <div className={style.flatSquare__price} itemScope={isSchema} itemProp={isSchema ? 'offers' : undefined} itemType={isSchema ? SCHEMA.OFFER : undefined}>
        <p className={isRedPrice ? style.flatSquare__amountRed : style.flatSquare__amount} itemProp={isSchema ? 'price' : undefined}>
          {formatLongPrice(flat?.currentPrice)} <span className={style.flatSquare__currency}>₽</span>
        </p>
        <div className={style.flatSquare__discount}>
          {isRedPrice ? <>
              <p className={style.flatSquare__discount__text}>
                {formatLongPrice(flat?.real_price)} <span className={style.flatSquare__currency}>₽</span>
              </p>
              <div className={style.icon}>
                <NextImage fill src={redPriceIcon.src} alt='квартира по акции' />
              </div>
            </> : <div className={style.icon}>
              <NextImage src={installmentIcon.src} fill alt='installment' />
            </div>}
        </div>
        {renderSchemaMetaOffer(isSchema, flat?.currentPrice)}
      </div>
    </RedPriceTooltip>;
};
const FlatCardSquare = flat => {
  const {
    bulk_id,
    number,
    isSchema
  } = flat;
  const flatStatus = useSelector(state => state?.filters?.flatStatus);
  const flatfavorites = useSelector(flatsLocalStorageSelector);
  const [isActive, setIsActive] = useState(false);
  const dispatch = useDispatch();
  const rooms = editRoomsLabel(flat);
  const roomsText = rooms?.rooms === 'Студия' ? rooms?.rooms : `${rooms?.rooms}-спальн.`;
  const isShowReservedFlat = flat?.status === flatStatus?.reserve && RESERVED_FLATS_31832;
  const image = useMemo(() => {
    if (bulk_id && number) {
      return `${URLS.CDN_PLANS}/${bulk_id}/flats/furnished-plan/${number}.svg`;
    }
    return '';
  }, [bulk_id, number]);
  useEffect(() => {
    if (flatfavorites?.filter(apartment => apartment?.id === flat?.id).length) {
      setIsActive(true);
    }
  }, [flat, flatfavorites]);
  const handleLocalStorage = event => {
    event.stopPropagation();
    event.preventDefault();
    if (isActive) {
      setIsActive(false);
      dispatch(removeLocalFlats(flat));
      return;
    }
    dispatch(setLocalFlats(flat));
    pushDataLayer('click_success_favorites');
  };
  const clsHeart = isActive ? style.flatSquare__heart__white : style.flatSquare__heart;
  const isReserved = flat?.status === flatStatus?.reserve;
  return <Link className={style.content} href={`/flat/${flat?.id}`} onClick={() => dispatch(setGenPlan(false))} itemScope={isSchema} itemProp={isSchema ? 'itemListElement' : undefined} itemType={isSchema ? SCHEMA.PRODUCT : undefined}>
      {isReserved && renderReservedStatus(RESERVED_FLATS_31832)}
      <div className={style.flatSquare}>
        <div className={style.flatSquare__street}>
          <div className={style.flatSquare__street_descr}>
            <p className={style.flatSquare__street_text}>
              <span>корп. {flat?.bulk?.number}</span>
            </p>
            {!SETTLEMENT_INFO_29919 && <p className={`${style.flatSquare__street_desc} ${style.flatSquare__street_deadline}`}>
                {changeDate(flat?.bulk.settlement_date)}
              </p>}
          </div>
          <button type='button' aria-label={isActive ? 'Убрать из избранного' : 'Добавить в избранное'} onClick={handleLocalStorage} className={cn(clsHeart, {
          [style.flatSquare__heart_reserved]: isShowReservedFlat
        })} data-testid={isActive ? 'favoriteRemove' : 'favoriteAdd'}>
            <svg width='16' height='15' viewBox='0 0 16 15' fill='none'>
              <path fillRule='evenodd' clipRule='evenodd' d='M3.62193 0.932344C2.11699 1.1563 1.0669 1.88035 0.467674 3.10716C0.0191533 4.02539 -0.126141 5.36995 0.116684 6.35531C0.352189 7.31105 0.916727 8.36299 1.78737 9.4684C2.86195 10.8327 4.52138 12.4145 6.3879 13.8536C6.70427 14.0975 7.83247 14.9179 7.96028 14.997C8.0051 15.0247 8.23372 14.8675 9.06233 14.239C11.349 12.5045 13.3121 10.6096 14.5125 8.97823C15.6437 7.44105 16.1109 6.15437 15.978 4.9431C15.7221 2.6119 13.8518 0.884884 11.5803 0.882358C10.4135 0.881046 9.34891 1.32791 8.49929 2.17562C8.37026 2.30438 8.20839 2.48264 8.13957 2.5718C8.07076 2.66096 8.00741 2.73391 7.99881 2.73391C7.99021 2.73391 7.92687 2.66096 7.85805 2.5718C7.3161 1.86967 6.4306 1.27259 5.57335 1.0312C5.05229 0.884468 4.22519 0.842574 3.62193 0.932344Z' fill={isActive ? '#FFFFFF' : '#979797'} />
            </svg>
          </button>
        </div>
        <NextImage src={image} alt='flat' className={style.flatSquare__img} width={0} height={0} itemProp={isSchema ? 'image' : undefined} />
        {isShowReservedFlat ? <p className={style.flatSquare__reservedText}>Квартира забронирована</p> : renderPriceBlock(flat, isSchema)}
        <div className={style.flatSquare__description_box} itemProp={isSchema ? 'description' : undefined}>
          <p className={style.flatSquare__desc_text}>{roomsText}</p>
          <p className={style.flatSquare__desc_text}>
            {flat?.area}
            <span>
              м<sup>2</sup>
            </span>
          </p>
          <p className={style.flatSquare__desc_text}>
            {flat?.floor} этаж из {flat?.section?.floorsCount}
          </p>
          <p className={style.flatSquare__desc_text} itemProp={isSchema ? 'name' : undefined}>
            № {flat?.number}
          </p>
        </div>
        <div className={style.flatSquare__special}>
          <ButtonsHideAmount flat={flat} amount={23} />
        </div>
      </div>
    </Link>;
};
export default FlatCardSquare;